import React from "react";
import { riskTextColor } from "@common/risk/riskPalette";

// The directional risk-level icons, each with a distinct color (no two levels share a color).

export enum RiskLevelIndicator {
    None = 0,
    TripleArrowDown = 1,
    DoubleArrowDown = 2,
    ArrowDown = 3,
    Equal = 4,
    ArrowUp = 5,
    DoubleArrowUp = 6,
    TripleArrowUp = 7,
}

// Single source of truth for each indicator's color: shared by the icons below and the matrix cell backgrounds.
export const colorByRiskLevelIndicator: Record<RiskLevelIndicator, string> = {
    [RiskLevelIndicator.None]: "#777777",
    [RiskLevelIndicator.TripleArrowDown]: "#009586",
    [RiskLevelIndicator.DoubleArrowDown]: "#2CD5C4",
    [RiskLevelIndicator.ArrowDown]: "#A6F7C3",
    [RiskLevelIndicator.Equal]: "#FFEF89",
    [RiskLevelIndicator.ArrowUp]: "#FFC1E7",
    [RiskLevelIndicator.DoubleArrowUp]: "#FF6DC7",
    [RiskLevelIndicator.TripleArrowUp]: "#F7447A",
};

export const colorByRiskLevelIndicatorBackgroundColor: Record<RiskLevelIndicator, string> = {
    [RiskLevelIndicator.None]: "#F2F2F2",
    [RiskLevelIndicator.TripleArrowDown]: "#D7EEEB",
    [RiskLevelIndicator.DoubleArrowDown]: "#DDFFFC",
    [RiskLevelIndicator.ArrowDown]: "#F4FFF8",
    [RiskLevelIndicator.Equal]: "#FFFDEF",
    [RiskLevelIndicator.ArrowUp]: "#FFF8FC",
    [RiskLevelIndicator.DoubleArrowUp]: "#FFECF7",
    [RiskLevelIndicator.TripleArrowUp]: "#FFDBE6",
};

export const noneRiskIcon = (
    <svg width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.07367e-05 1L10 1" stroke={colorByRiskLevelIndicator[RiskLevelIndicator.None]} strokeWidth="2" />
    </svg>
);

export const tripleArrowDownRiskIcon = (
    <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M10 5.20819L5.00018 9.20725L0.00012207 5.20819V8.15019L5.00018 12.1492L10 8.15019V5.20819Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.TripleArrowDown]}
        />
        <path d="M10 0L5.00018 3.99906L0.00012207 0V2.942L5.00018 6.94106L10 2.942V0Z" fill={colorByRiskLevelIndicator[RiskLevelIndicator.TripleArrowDown]} />
        <path
            d="M5.00005 14.3993L0 10.4L0 13.3423L5.00005 17.3413L9.99988 13.3423V10.4L5.00005 14.3993Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.TripleArrowDown]}
        />
    </svg>
);

export const doubleArrowDownRiskIcon = (
    <svg width="10" height="13" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M10 0L5.00018 4.22967L0.00012207 0V3.11165L5.00018 7.34133L10 3.11165V0Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.DoubleArrowDown]}
        />
        <path
            d="M5.00006 9.72112L0 5.49121V8.60311L5.00006 12.8328L9.99988 8.60311V5.49121L5.00006 9.72112Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.DoubleArrowDown]}
        />
    </svg>
);

export const arrowDownRiskIcon = (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.00012 4.22991L0 0V3.11189L5.00012 7.34157L10 3.11189V0L5.00012 4.22991Z" fill={colorByRiskLevelIndicator[RiskLevelIndicator.ArrowDown]} />
    </svg>
);

export const equalsRiskIcon = (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 0H5.12204H0V3L5.12204 2.99977L10 3V0Z" fill={colorByRiskLevelIndicator[RiskLevelIndicator.Equal]} />
        <path d="M10 5H5.12204H0V8L5.12204 7.99977L10 8V5Z" fill={colorByRiskLevelIndicator[RiskLevelIndicator.Equal]} />
    </svg>
);

export const arrowUpRiskIcon = (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.00012 0L0 4.60928V8L5.00012 3.39099L10 8V4.60928L5.00012 0Z" fill={colorByRiskLevelIndicator[RiskLevelIndicator.ArrowUp]} />
    </svg>
);

export const doubleArrowUpRiskIcon = (
    <svg width="10" height="13" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M0 12.8328L4.99982 8.60311L9.99988 12.8328V9.72112L4.99982 5.49145L0 9.72112V12.8328Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.DoubleArrowUp]}
        />
        <path
            d="M4.99995 3.11166L10 7.34157V4.22967L4.99995 0L0.000121117 4.22967V7.34157L4.99995 3.11166Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.DoubleArrowUp]}
        />
    </svg>
);

export const tripleArrowUpRiskIcon = (
    <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M1.90735e-06 12.1331L4.99982 8.13406L9.99988 12.1331V9.19112L4.99982 5.19206L1.90735e-06 9.19112V12.1331Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.TripleArrowUp]}
        />
        <path
            d="M1.90735e-06 17.3413L4.99982 13.3423L9.99988 17.3413V14.3993L4.99982 10.4003L1.90735e-06 14.3993V17.3413Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.TripleArrowUp]}
        />
        <path
            d="M4.99995 2.942L10 6.94129V3.99906L4.99995 0L0.000123024 3.99906V6.94129L4.99995 2.942Z"
            fill={colorByRiskLevelIndicator[RiskLevelIndicator.TripleArrowUp]}
        />
    </svg>
);

export function riskLevelIndicatorImage(indicator: RiskLevelIndicator): React.ReactNode {
    switch (indicator) {
        case RiskLevelIndicator.None:
            return noneRiskIcon;
        case RiskLevelIndicator.TripleArrowDown:
            return tripleArrowDownRiskIcon;
        case RiskLevelIndicator.DoubleArrowDown:
            return doubleArrowDownRiskIcon;
        case RiskLevelIndicator.ArrowDown:
            return arrowDownRiskIcon;
        case RiskLevelIndicator.Equal:
            return equalsRiskIcon;
        case RiskLevelIndicator.ArrowUp:
            return arrowUpRiskIcon;
        case RiskLevelIndicator.DoubleArrowUp:
            return doubleArrowUpRiskIcon;
        case RiskLevelIndicator.TripleArrowUp:
            return tripleArrowUpRiskIcon;
    }
}

export interface RiskLevelChipStyle {
    color: { backgroundColor: string; color: string; borderColor: string };
    icon: React.ReactNode;
}

// The chip visuals for a risk level: border + icon take the indicator's vivid color, background takes its light
// tint, and the icon is the configured arrow. Mirrors RiskAreaMatrix.utils#riskCellColor's indicators[level-1]
// lookup, so a chip and its matrix cell always agree. Returns null when the level has no configured indicator.
export function getRiskLevelIndicatorChipStyle(level: number, riskLevelIndicators: RiskLevelIndicator[]): RiskLevelChipStyle | null {
    const indicator = riskLevelIndicators[level - 1];
    if (indicator === undefined) return null;

    return {
        color: {
            backgroundColor: colorByRiskLevelIndicatorBackgroundColor[indicator],
            color: riskTextColor,
            borderColor: colorByRiskLevelIndicator[indicator],
        },
        icon: riskLevelIndicatorImage(indicator),
    };
}

// The indicators offered in the picker, ordered highest risk → lowest, with None (no icon) at the bottom.
export const riskLevelIndicatorChoices: RiskLevelIndicator[] = [
    RiskLevelIndicator.TripleArrowUp,
    RiskLevelIndicator.DoubleArrowUp,
    RiskLevelIndicator.ArrowUp,
    RiskLevelIndicator.Equal,
    RiskLevelIndicator.ArrowDown,
    RiskLevelIndicator.DoubleArrowDown,
    RiskLevelIndicator.TripleArrowDown,
    RiskLevelIndicator.None,
];

// Distinct default indicator per level per scale (no bucketing), matching the work item's arrow specification.
const defaultIndicatorsByScale: Record<number, RiskLevelIndicator[]> = {
    3: [RiskLevelIndicator.ArrowDown, RiskLevelIndicator.Equal, RiskLevelIndicator.ArrowUp],
    4: [RiskLevelIndicator.DoubleArrowDown, RiskLevelIndicator.ArrowDown, RiskLevelIndicator.ArrowUp, RiskLevelIndicator.DoubleArrowUp],
    5: [
        RiskLevelIndicator.DoubleArrowDown,
        RiskLevelIndicator.ArrowDown,
        RiskLevelIndicator.Equal,
        RiskLevelIndicator.ArrowUp,
        RiskLevelIndicator.DoubleArrowUp,
    ],
    6: [
        RiskLevelIndicator.DoubleArrowDown,
        RiskLevelIndicator.ArrowDown,
        RiskLevelIndicator.Equal,
        RiskLevelIndicator.ArrowUp,
        RiskLevelIndicator.DoubleArrowUp,
        RiskLevelIndicator.TripleArrowUp,
    ],
};

export function getDefaultRiskAreaLevelIndicators(scale: number): RiskLevelIndicator[] {
    return defaultIndicatorsByScale[scale] ?? [];
}
